import { useServiceProxy } from 'hooks/kong'
import { User } from 'types/auth'
import { YesNo } from 'types/global'
import { ClaimResponse, ClaimServiceRequest, SplunkResponse } from 'types/ldrClaim'
import { TransformedPickupReturnInfo } from 'types/ticket'
import { useGetDuplicateLDREvent } from './useGetDuplicateLDREvent'
import { useSelector } from 'react-redux'
import { selectTicket } from 'redux/appStore'
import { isEmpty } from 'lodash-es'

export const useCreateLDRClaim = () => {
  const serviceProxy = useServiceProxy()
  const findDuplicateLDR = useGetDuplicateLDREvent()
  const currentTicket = useSelector(selectTicket)

  return async function (
    claim: any,
    thirdParty: any,
    ticketNumber: string,
    pickupInfo: TransformedPickupReturnInfo,
    currentUser: User | null,
    lossCountryIsoCode: string,
  ) {
    const { userFirstName, userLastName } = splitUserName(currentUser ? currentUser.name : '')
    const hasPoliceReport = claim && claim.policeReport ? claim.policeReport.toUpperCase() === YesNo.YES : false
    const hasThirdPartyInsurance =
      thirdParty && thirdParty.thirdPartyInsurance ? thirdParty.thirdPartyInsurance.toUpperCase() === YesNo.YES : false
    const duplicateClaim = await findDuplicateLDR(claim, currentTicket)

    const [month, day, year] = claim.date.split('/')
    const lossDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00`

    const policeReport = hasPoliceReport
      ? {
          reportNumber: claim.policeReportNumber,
          precinctDescription: claim.precinctName,
          departmentName: claim.policeDepartmentName,
        }
      : {}

    const thirdPartyInfo = hasThirdPartyInsurance
      ? {
          insurance: {
            companyName: thirdParty.companyName,
            insuranceClaimNumber: thirdParty.claimNumber,
          },
        }
      : {}

    const claimBody = {
      claimLevelInformation: {
        branchLegacyIorgId: pickupInfo.groupBranchId,
        claimTypeId: 1,
        claimWorkFlowStageTypeId: 2,
        factsOfLoss: claim.facts,
        groupLegacyIorgId: pickupInfo.groupBranchId.substring(0, 2),
        incidentTypeId: 13,
        lossCountryIsoCode,
        lossDate,
        lossState: claim.state.value,
        accidentReportedByUserFirstName: userFirstName,
        accidentReportedByUserLastName: userLastName,
        accidentReportedByUserId: currentUser?.nameID,
        accidentReportedApplication: 'ROADSIDE',
        accidentReportedDate: new Date().toISOString().split('.')[0], //Ex: “2026-07-31T15:10:00”
      },
      onRent: true,
      rentalContract: {
        rentalContractNumber: ticketNumber,
      },
      firstPartyVehicleInformation: {
        vehicle: {
          vin: claim.vehicle.vin,
          towed: false,
        },
      },
      policeDepartments: [policeReport],
      questionsInformation: [
        {
          questionTypeId: 11,
          answerIndicator: hasPoliceReport,
        },
        {
          questionTypeId: 7,
          answerIndicator: hasThirdPartyInsurance,
        },
      ],
      thirdPartyVehiclesInformation: [thirdPartyInfo],
    } as ClaimServiceRequest

    const duplicateAlertPayload = {
      alertType: 'LDR_DUPLICATE',
      sendEmail: true,
      message: `Duplicate LDR detected for ticket ${currentTicket?.ticketNumber}`,
      ticketNumber: currentTicket?.ticketNumber,
      claimNumber: duplicateClaim?.attributes?.claimNumber,
    }

    try {
      if (duplicateClaim && !isEmpty(duplicateClaim)) {
        //create the splunk alert
        await serviceProxy<SplunkResponse>('post', `/serviceproxy/alert`, {}, duplicateAlertPayload)

        //return a spoof ClaimResponse so the UI doesn't think it's an error
        return { data: { errors: [], warns: [], claimNumber: duplicateClaim?.attributes?.claimNumber, claimId: '' } }
      } else {
        return await serviceProxy<ClaimResponse>('post', `/serviceproxy/claimService`, {}, claimBody)
      }
    } catch (error) {
      console.log(error)
    }
  }
}

function splitUserName(name: string) {
  if (name === '') return { userFirstName: '', userLastName: '' }

  const nameSplit = name.split(',')
  const userFirstName = nameSplit[1].split(' ')[1] //Removes middle initial, first array element is leading space
  const userLastName = nameSplit[0]

  return { userFirstName, userLastName }
}

export default useCreateLDRClaim
